import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Spinner } from './components/ui';
import './App.css';

import Landing from './pages/Landing';
const AuthPage = lazy(() => import('./pages/AuthPage'));
const GoogleCallbackPage = lazy(() => import('./pages/GoogleCallbackPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ChatCoachPage = lazy(() => import('./pages/ChatCoachPage'));
const ProfileOptimizerPage = lazy(() => import('./pages/ProfileOptimizerPage'));
const VoicePage = lazy(() => import('./pages/VoicePage'));
const PlanPage = lazy(() => import('./pages/PlanPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

function FullLoader() {
  return <div className="min-h-screen bg-bg flex items-center justify-center"><Spinner className="w-8 h-8 text-ember" /></div>;
}

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to={`/auth?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (!user.email_verified) return <Navigate to="/auth/verify-email" replace />;
  if (admin && !user.is_admin) return <Navigate to="/app" replace />;
  if (!admin && !user.onboarding_complete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

export default function App() {
  // Capture referral code from any entry URL (e.g. yuloapp.com/?ref=ABC123)
  const refParam = new URLSearchParams(window.location.search).get('ref');
  if (refParam) localStorage.setItem('yc_ref', refParam);
  return (
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Toaster theme="light" position="top-center" toastOptions={{ style: { background: '#FFFFFF', border: '1px solid #EAE6DF', color: '#1F1D1A', boxShadow: '0 8px 30px rgba(31,29,26,0.08)' } }} />
        <Suspense fallback={<FullLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<LegalPage page="terms" />} />
            <Route path="/privacy" element={<LegalPage page="privacy" />} />
            <Route path="/refund" element={<LegalPage page="refund" />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
            <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
            <Route path="/onboarding" element={<Protected admin={false}><OnboardingPage /></Protected>} />
            <Route path="/app" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/app/chat" element={<Protected><ChatCoachPage /></Protected>} />
            <Route path="/app/profile" element={<Protected><ProfileOptimizerPage /></Protected>} />
            <Route path="/app/voice" element={<Protected><VoicePage /></Protected>} />
            <Route path="/app/plan" element={<Protected><PlanPage /></Protected>} />
            <Route path="/app/billing" element={<Protected><BillingPage /></Protected>} />
            <Route path="/app/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/app/admin" element={<Protected admin><AdminPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
